import { useState } from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Plus, Stethoscope } from 'lucide-react';
import { useOptiBaySessions, useCreateOptiBaySession, useUpdateOptiBaySession } from '@/hooks/useOptiBaySessions';
import { OptiBayStageTracker } from './OptiBayStageTracker';
import { OptiBaySymptomStep } from './OptiBaySymptomStep';
import { OptiBaySystemFamilyStep } from './OptiBaySystemFamilyStep';
import { OptiBayTestPathStep } from './OptiBayTestPathStep';
import { OptiBayEvidenceStep } from './OptiBayEvidenceStep';
import { OptiBayGuidanceStep } from './OptiBayGuidanceStep';
import { OptiBayVerificationStep } from './OptiBayVerificationStep';
import { OptiBayInterpretationStep } from './OptiBayInterpretationStep';
import { OptiBayRepairStep } from './OptiBayRepairStep';
import { OptiBayVerifyStep } from './OptiBayVerifyStep';
import { OptiBayTrainingStep } from './OptiBayTrainingStep';
import type { Ticket } from '@/types';
import type { OptiBaySession, OptiBayStage } from '@/types/optibay';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';

interface OptiBayDiagnosticsTabProps {
  ticket: Ticket;
  className?: string;
}

export function OptiBayDiagnosticsTab({ ticket, className }: OptiBayDiagnosticsTabProps) {
  const shopId = (ticket as any).shop_id || '';
  const techId = (ticket as any).technician_id || null;
  const techProfileId = (ticket as any).technician_profile_id || null;

  // Core-100 R2 CORE-AE-GAP-003: a failed read is not "no sessions". Treating it as empty offered
  // "Start Diagnostic Session" over an unreadable history — a second session for the same ticket.
  const { data: sessions = [], isLoading, isError: sessionsUnreadable, refetch: refetchSessions } = useOptiBaySessions(ticket.id);
  const [startError, setStartError] = useState<string | null>(null);
  const createSession = useCreateOptiBaySession();
  const updateSession = useUpdateOptiBaySession();

  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);

  const activeSession = sessions.find(s => s.id === activeSessionId) || sessions.find(s => s.status === 'active') || null;

  const handleStartSession = async () => {
    try {
      const newSession = await createSession.mutateAsync({
        ticketId: ticket.id,
        shopId,
        technicianId: techId,
        technicianProfileId: techProfileId,
      });
      setActiveSessionId(newSession.id);
    } catch (e) {
      console.error('Failed to create OptiBay session:', e);
      setStartError('The diagnostic session could not be started. Nothing was created; try again.');
    }
  };

  const handleStageChange = async (stage: OptiBayStage) => {
    if (!activeSession) return;
    await updateSession.mutateAsync({
      sessionId: activeSession.id,
      updates: { current_stage: stage },
    });
  };

  const handleFamilySelect = async (familyId: string, subfamilyId: string | null) => {
    if (!activeSession) return;
    await updateSession.mutateAsync({
      sessionId: activeSession.id,
      updates: {
        system_family_id: familyId,
        subfamily_id: subfamilyId,
        current_stage: 'test_paths',
      },
    });
  };

  const renderCurrentStep = () => {
    if (!activeSession) return null;
    switch (activeSession.current_stage) {
      case 'symptom':
        return (
          <OptiBaySymptomStep
            session={activeSession}
            onNext={() => handleStageChange('system_family')}
          />
        );
      case 'system_family':
        return (
          <OptiBaySystemFamilyStep
            session={activeSession}
            onSelect={handleFamilySelect}
            onBack={() => handleStageChange('symptom')}
          />
        );
      case 'test_paths':
        return (
          <OptiBayTestPathStep
            session={activeSession}
            onNext={() => handleStageChange('evidence')}
            onBack={() => handleStageChange('system_family')}
          />
        );
      case 'evidence':
        return (
          <OptiBayEvidenceStep
            session={activeSession}
            onNext={() => handleStageChange('guidance')}
            onBack={() => handleStageChange('test_paths')}
          />
        );
      case 'guidance':
        return (
          <OptiBayGuidanceStep
            session={activeSession}
            onNext={() => handleStageChange('verification_tests')}
            onBack={() => handleStageChange('evidence')}
          />
        );
      case 'verification_tests':
        return (
          <OptiBayVerificationStep
            session={activeSession}
            onNext={() => handleStageChange('interpretation')}
            onBack={() => handleStageChange('guidance')}
          />
        );
      case 'interpretation':
        return (
          <OptiBayInterpretationStep
            session={activeSession}
            onNext={() => handleStageChange('repair')}
            onBack={() => handleStageChange('verification_tests')}
          />
        );
      case 'repair':
        return (
          <OptiBayRepairStep
            session={activeSession}
            onNext={() => handleStageChange('verify')}
            onBack={() => handleStageChange('interpretation')}
          />
        );
      case 'verify':
        return (
          <OptiBayVerifyStep
            session={activeSession}
            onNext={() => handleStageChange('training')}
            onBack={() => handleStageChange('repair')}
          />
        );
      case 'training':
        return (
          <OptiBayTrainingStep
            session={activeSession}
            onBack={() => handleStageChange('verify')}
            onComplete={() => setActiveSessionId(null)}
          />
        );
      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            <p className="text-sm">This stage will be available in a future phase.</p>
          </div>
        );
    }
  };

  return (
    <ScrollArea className={cn('h-[calc(85vh-160px)]', className)}>
      <div className="px-6 py-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Stethoscope className="h-4 w-4 text-primary" />
            <h3 className="font-semibold text-sm">Diagnostic Sessions</h3>
            <Badge variant="secondary" className="text-xs">{sessions.length}</Badge>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={handleStartSession}
            disabled={createSession.isPending}
          >
            <Plus className="h-3 w-3 mr-1" />
            New Session
          </Button>
        </div>

        {/* Session list (if multiple) */}
        {sessions.length > 1 && (
          <div className="flex gap-2 overflow-x-auto scrollbar-none pb-1">
            {sessions.map(s => (
              <button
                key={s.id}
                type="button"
                onClick={() => setActiveSessionId(s.id)}
                className={cn(
                  'shrink-0 rounded-lg border px-3 py-1.5 text-xs font-medium transition-colors',
                  (activeSession?.id === s.id)
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'hover:bg-accent/50 text-muted-foreground'
                )}
              >
                {format(new Date(s.created_at), 'MMM d, h:mm a')}
                {s.status !== 'active' && (
                  <Badge variant="secondary" className="ml-1.5 text-[10px]">{s.status}</Badge>
                )}
              </button>
            ))}
          </div>
        )}

        {/* Active session content */}
        {isLoading ? (
          <div className="text-sm text-muted-foreground py-8 text-center">Loading sessions…</div>
        ) : sessionsUnreadable ? (
          <div className="text-center py-12 space-y-3" data-testid="diagnostics-sessions-unreadable">
            <p className="text-sm text-muted-foreground">Diagnostic sessions could not be loaded. This is not the same as having none.</p>
            <Button variant="outline" onClick={() => void refetchSessions()}>Retry</Button>
          </div>
        ) : activeSession ? (
          <div className="space-y-4">
            <OptiBayStageTracker
              currentStage={activeSession.current_stage as OptiBayStage}
              onStageClick={handleStageChange}
            />
            <div className="border rounded-lg p-4">
              {renderCurrentStep()}
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-center py-12 space-y-3">
            <Stethoscope className="h-10 w-10 mx-auto text-muted-foreground/50" />
            <p className="text-sm text-muted-foreground">No diagnostic sessions yet</p>
            {startError && <p className="text-sm text-destructive" data-testid="diagnostics-start-error">{startError}</p>}
            <Button onClick={handleStartSession} disabled={createSession.isPending}>
              <Plus className="h-4 w-4 mr-2" />
              Start Diagnostic Session
            </Button>
          </div>
        ) : null}
      </div>
    </ScrollArea>
  );
}
